import type { ApexOptions } from 'apexcharts';
import type { PieChartComponent } from '../types';

import React, { lazy, Suspense } from 'react';
import { chartCardSx, chartTitleSx, chartPalette, baseChartOptions } from '../chart-style';

import { alpha } from '@mui/material/styles';
import { Box, Card, useTheme, Typography, useMediaQuery } from '@mui/material';

const LazyApex = lazy(() => import('react-apexcharts'));

export interface PieChartProps {
  component: PieChartComponent;
}

export function PieChart({ component }: PieChartProps): React.JSX.Element {
  const theme = useTheme();
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const isDonut = component.variant === 'donut';
  const chartType = isDonut ? 'donut' : 'pie';
  const labels = component.slices.map((s) => s.label);
  const values = component.slices.map((s) => s.value);
  const palette = chartPalette(theme);
  const surface = theme.palette.container?.medium ?? theme.palette.grey[800];

  const base = baseChartOptions(theme, reduceMotion);
  const options: ApexOptions = {
    ...base,
    chart: { ...base.chart, type: chartType },
    colors: palette,
    labels,
    fill: { type: 'solid', opacity: 1, colors: palette },
    stroke: { width: 2, colors: [surface] },
    dataLabels: {
      enabled: true,
      style: {
        colors: [theme.palette.common.white],
        fontSize: '12px',
        fontWeight: 600,
        fontFamily: theme.typography.fontFamily as string,
      },
      dropShadow: {
        enabled: true,
        top: 0,
        left: 0,
        blur: 3,
        color: theme.palette.common.black,
        opacity: 0.45,
      },
    },
    plotOptions: isDonut
      ? {
          pie: {
            donut: {
              size: '72%',
              labels: {
                show: true,
                total: {
                  show: true,
                  color: alpha(theme.palette.common.white, 0.7),
                  fontSize: '12px',
                  fontWeight: 500,
                  fontFamily: theme.typography.fontFamily as string,
                  label: 'Total',
                },
                value: {
                  color: theme.palette.common.white,
                  fontSize: '24px',
                  fontWeight: 600,
                  fontFamily: theme.typography.fontFamily as string,
                },
              },
            },
          },
        }
      : undefined,
    legend: {
      show: true,
      position: 'bottom',
      labels: { colors: theme.palette.common.white },
      fontFamily: theme.typography.fontFamily as string,
      fontWeight: 500,
      itemMargin: { horizontal: 12, vertical: 4 },
      markers: { size: 6, strokeWidth: 0 },
    },
    tooltip: { ...base.tooltip, fillSeriesColor: false },
  };

  return (
    <Card data-testid="ari.bot-ui.pie-chart" elevation={0} sx={chartCardSx(theme)}>
      {component.title && (
        <Typography variant="h6" sx={chartTitleSx(theme)}>
          {component.title}
        </Typography>
      )}
      <Box sx={{ width: '100%', flex: 1, minHeight: 0, position: 'relative' }}>
        <Suspense fallback={null}>
          <LazyApex type={chartType} series={values} options={options} width="100%" height="100%" />
        </Suspense>
      </Box>
    </Card>
  );
}
